// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ShieldCheck, ShieldAlert } from 'lucide-react'
import * as api from '../../api/client'

/**
 * Account security — TOTP two-factor setup. Reachable by any authenticated user
 * (the Settings hub tab has no permission gate), since external providers enroll here too.
 */
export default function SecuritySettings() {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [enroll, setEnroll] = useState<api.MfaEnrollResponse | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  async function loadStatus() {
    try { const s = await api.mfaStatus(); setEnabled(s.enabled) }
    catch { /* not authenticated / no row → treat as off */ }
    finally { setLoading(false) }
  }
  useEffect(() => { void loadStatus() }, [])

  async function startEnroll() {
    setBusy(true); setError(null); setNotice(null)
    try { setEnroll(await api.mfaEnroll()) }
    catch { setError('Could not start enrollment. Please try again.') }
    finally { setBusy(false) }
  }

  async function confirm() {
    if (code.length < 6) return
    setBusy(true); setError(null)
    try {
      await api.mfaConfirm(code)
      setEnabled(true); setEnroll(null); setCode('')
      setNotice('Two-factor authentication is now on. You\u2019ll be asked for a code at each sign-in.')
    } catch (e: any) {
      setError(e?.body?.error === 'invalid_code'
        ? 'That code is incorrect. Check the app and enter the current one.'
        : 'Could not confirm the code. Please try again.')
    } finally { setBusy(false) }
  }

  async function disable() {
    if (code.length < 6) { setError('Enter a current code from your app to turn it off.'); return }
    setBusy(true); setError(null)
    try {
      await api.mfaDisable(code)
      setEnabled(false); setCode('')
      setNotice('Two-factor authentication is now off.')
    } catch (e: any) {
      setError(e?.body?.error === 'invalid_code' ? 'That code is incorrect.' : 'Could not turn it off. Please try again.')
    } finally { setBusy(false) }
  }

  const codeInput = (
    <input
      inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000"
      value={code}
      onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      style={{
        fontSize: 22, letterSpacing: 8, textAlign: 'center', padding: '10px', width: 180,
        borderRadius: 8, border: '1px solid var(--color-border)', fontFamily: 'monospace',
      }}
    />
  )

  if (loading) return <div style={{ padding: 24, color: 'var(--color-text-mute)' }}>Loading…</div>

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>Two-factor authentication</h2>
      <p style={{ fontSize: 14, color: 'var(--color-text-soft)', margin: '0 0 20px' }}>
        Add a one-time code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, etc.)
        as a second step at sign-in.
      </p>

      {notice && <div style={{ background: 'var(--color-success-bg, #ecfdf5)', color: 'var(--color-success, #047857)', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{notice}</div>}
      {error && <div style={{ background: 'var(--color-danger-bg, #fef2f2)', color: 'var(--color-danger, #dc2626)', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Enabled state */}
      {enabled && !enroll && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-success, #047857)', fontWeight: 600, marginBottom: 12 }}>
            <ShieldCheck size={18} /> Two-factor is on
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)', marginBottom: 10 }}>
            To turn it off, enter a current code from your authenticator app.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {codeInput}
            <button onClick={disable} disabled={busy || code.length < 6}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--color-danger, #dc2626)', background: 'transparent', color: 'var(--color-danger, #dc2626)', fontWeight: 600, cursor: busy || code.length < 6 ? 'default' : 'pointer', opacity: busy || code.length < 6 ? 0.6 : 1 }}>
              Turn off
            </button>
          </div>
        </div>
      )}

      {/* Not enabled, not yet enrolling */}
      {!enabled && !enroll && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-soft)', marginBottom: 12 }}>
            <ShieldAlert size={18} /> Two-factor is off
          </div>
          <button onClick={startEnroll} disabled={busy}
            style={{ padding: '11px 16px', borderRadius: 8, border: 'none', background: 'var(--color-accent, #2563eb)', color: '#fff', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Starting…' : 'Set up two-factor'}
          </button>
        </div>
      )}

      {/* Enrolling: show QR + manual key + confirm */}
      {enroll && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 16 }}>
          <ol style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 14, color: 'var(--color-text-soft)', lineHeight: 1.6 }}>
            <li>Open your authenticator app and add a new account.</li>
            <li>Scan this QR code (or enter the key manually).</li>
            <li>Enter the 6-digit code it shows to confirm.</li>
          </ol>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid var(--color-border)' }}>
              <QRCodeSVG value={enroll.otpauth_uri} size={168} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 200 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 4 }}>Can&rsquo;t scan? Enter this key:</div>
              <code style={{ display: 'block', wordBreak: 'break-all', fontSize: 13, background: 'var(--color-surface-2, #f3f4f6)', padding: '8px 10px', borderRadius: 6, marginBottom: 14 }}>{enroll.secret}</code>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {codeInput}
                <button onClick={confirm} disabled={busy || code.length < 6}
                  style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: 'var(--color-accent, #2563eb)', color: '#fff', fontWeight: 600, cursor: busy || code.length < 6 ? 'default' : 'pointer', opacity: busy || code.length < 6 ? 0.6 : 1 }}>
                  {busy ? 'Confirming…' : 'Confirm'}
                </button>
              </div>
              <button onClick={() => { setEnroll(null); setCode(''); setError(null) }}
                style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--color-text-mute)', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
